import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FImage from 'components/BaseComponents/FImage'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import SInput from 'components/opinionless/SInputV2'
import STextWithTooltip from 'components/opinionless/STextWithTooltip'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBBox, LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Select from 'react-select'
import { StepWizardChildProps } from 'react-step-wizard'
import sSelectStyle from 'styles/select'
import { SStyled, useSTheme } from 'styles/theme'
import { useAddApplicationAnswersMutation, useSaveHouseholdMembersMutation } from 'types/graphql'
import { ga4_useTitle } from 'utils/ga4'
import XBanner from 'v2/components/XBanner'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import style from './style.module.scss'

const SBoxDropdown = SStyled(SBox)`

${(p) => `
    @media screen and (max-width: ${p.theme.breakpoints[0]}) {
        max-width: 100%;
    }
`}
`

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

interface Props {
    setCurrentStep?: (number) => void
    applicationId: string
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

const HouseholdIncomeMembers: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()
    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()

    useEffect(() => {
        refetchApplicationAnswers()
    }, [refetchApplicationAnswers])
    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const { handleSubmit, errors, setError, control, clearErrors, register, getValues, setValue } = useForm()
    const [addApplicationAnswers, { loading: addApplicationAnswersLoading }] = useAddApplicationAnswersMutation()
    const [saveHouseholdMembers, { loading: saveHouseholdMembersLoading }] = useSaveHouseholdMembersMutation()
    const [householdMemberNumber, setHouseholdMemberNumber] = useState(null)
    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])

    const [loading, setLoading] = useState(true)

    const requiredString = 'required_field'
    const fontFamily = props.fontFamily

    const householdMembersOptions = Array.from({ length: 14 }, (_, i) => i + 1).map((householdMembers) => ({
        value: householdMembers,
        label: householdMembers?.toString(),
    }))
    const relationshipDropdown = [
        'ssi_benefits_wizard_relationship_spouse',
        'ssi_benefits_wizard_relationship_child',
        'ssi_benefits_wizard_relationship_parent',
        'ssi_benefits_wizard_relationship_other',
    ]
    const relationShipDropdownOptions = useMemo(() => {
        return relationshipDropdown.map((relationship) => ({
            value: relationship,
            label: t(relationship),
        }))
    }, [t])

    ga4_useTitle('Household Income Members')

    useEffect(() => {
        if (!householdMembersLoading) {
            setHouseholdMemberNumber(applicationContext.householdMembers.length)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                }))
            )
            setLoading(false)
        }
    }, [applicationContext.householdMembers])

    const handleHouseholdMemberDetails = (selectedNumber = null) => {
        setHouseholdMembersDetails((prevDetails) => {
            const updatedDetails = [...prevDetails]
            const selectedHouseholdMembersNumber = selectedNumber === null ? updatedDetails.length : selectedNumber

            const updatedMembers = updatedDetails.slice(0, selectedHouseholdMembersNumber)

            for (let index = 0; index < selectedHouseholdMembersNumber; index++) {
                const memberFullName = getValues(`householdMemberFullName-${index}`)
                const memberRelationship = getValues(`lbHouseholdMemberRelationship-${index}`)?.value
                const memberId = updatedMembers[index]?.id
                const memberDob = updatedMembers[index]?.dob

                const isMemberSame =
                    memberId &&
                    memberFullName === updatedMembers[index]?.name &&
                    memberRelationship === updatedMembers[index]?.relation &&
                    memberDob === updatedMembers[index]?.dob

                if (!isMemberSame) {
                    updatedMembers[index] = {
                        id: memberId,
                        name: memberFullName,
                        relation: memberRelationship,
                        dob: memberDob,
                    }
                }
            }

            for (let index = selectedHouseholdMembersNumber; index < updatedDetails.length; index++) {
                setValue(`householdMemberFullName-${index}`, '')
                setValue(`lbHouseholdMemberRelationship-${index}`, '')
            }

            return updatedMembers
        })
    }

    const handleApplicationAnswersAndHouseholdMembers = () => {
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'HOUSEHOLD_MEMBERS',
                        response: (householdMemberNumber + 1).toString(),
                    },
                ],
            },
        }).then((response) => {
            if (response.data.addApplicationAnswers.success === true) {
                saveHouseholdMembers({
                    variables: {
                        personId: applicationContext.profile.person.id,
                        householdMembers: householdMembersDetails,
                    },
                }).then((response) => {
                    if (response.data.saveHouseholdMembers.success === true) {
                        props.nextStep()
                    } else {
                        setError('householdIncomeMembers', { type: 'oops', message: t('error') })
                    }
                })
            } else {
                setError('householdIncomeMembers', { type: 'oops', message: t('error') })
            }
        })
    }

    const householdMembersResponse = useMemo(() => {
        return getAnswersByCategory('HOUSEHOLD_MEMBERS')?.[0]?.response
    }, [applicationContext.answers])

    useEffect(() => {
        setValue('lbHouseholdMembersNumber', {
            value: householdMembersResponse,
            label: householdMembersResponse?.toString(),
        })
    }, [householdMembersResponse])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.members,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }
    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(handleApplicationAnswersAndHouseholdMembers)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                    }}
                >
                    {t('long_beach_household_income_members_subtitle')}
                </LBText>
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                        marginBottom: '24px',
                    }}
                    dangerouslySetInnerHTML={{ __html: t('long_beach_household_income_members_description') }}
                />
                <XBanner
                    title={t('long_beach_household_income_members_banner_title')}
                    description={t('long_beach_household_income_introduction_members_description')}
                    isButton={false}
                    type="warning"
                />
                <LBBox sx={{ display: 'flex !important', flexDirection: 'column !important' }}>
                    <SBox sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'left' }}>
                        <STextWithTooltip
                            listStyle={'none'}
                            text={t('long_beach_household_members_number_label')}
                            textStyle={{
                                color: theme.colors.blackLabelfont,
                                fontFamily: fontFamily.body,
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                lineHeight: '140%',
                                fontWeight: '700',
                                fontSize: '14px',
                                opacity: '80%',
                            }}
                        />
                    </SBox>
                    <SBoxDropdown>
                        <Controller
                            name="lbHouseholdMembersNumber"
                            control={control}
                            defaultValue={
                                householdMembersResponse
                                    ? {
                                          value: householdMembersResponse,
                                          label: householdMembersResponse?.toString(),
                                      }
                                    : null
                            }
                            rules={{ required: 'required_field' as string }}
                            render={(props: any) => {
                                const { onChange, value } = props
                                return (
                                    <Select
                                        name="lbHouseholdMembersNumberField"
                                        id="lbHouseholdMembersNumberField"
                                        options={householdMembersOptions}
                                        placeholder={t('long_beach_household_members_number_placeholder')}
                                        components={{ IndicatorSeparator: () => null }}
                                        onChange={(option: any) => {
                                            onChange(option)
                                            clearErrors()
                                            setHouseholdMemberNumber(option.value - 1)
                                            handleHouseholdMemberDetails(option.value - 1)
                                        }}
                                        value={value}
                                        theme={(theme) => ({
                                            ...theme,
                                            colors: {
                                                ...theme.colors,
                                                primary: theme.colors.inputBorder,
                                                neutral30: theme.colors.inputBorder,
                                            },
                                        })}
                                        styles={{
                                            ...sSelectStyle(theme),
                                            container: (provided, state) => ({
                                                ...sSelectStyle(theme).container(provided, state),
                                                flex: 2,
                                            }),
                                            control: (provided, state) => ({
                                                ...sSelectStyle(theme).control(provided, state),
                                                fontFamily: fontFamily.body,
                                                borderRadius: '8px',
                                                borderColor: errors.lbHouseholdMembersNumber
                                                    ? theme.colors.redError
                                                    : theme.colors.inputBorder,
                                            }),
                                            option: (provided, state) => ({
                                                ...sSelectStyle(theme).option(provided, state),
                                                textAlignLast: 'left',
                                                whiteSpace: 'pre-line',
                                                lineHeight: '140%',
                                                fontWeight: '400',
                                                fontSize: '14px',
                                                fontFamily: fontFamily.body,
                                                color: theme.colors.grayLabelPlaceholder,
                                                opacity: '80%',
                                            }),
                                            placeholder: (provided, state) => ({
                                                ...sSelectStyle(theme).option(provided, state),
                                                textAlignLast: 'left',
                                                whiteSpace: 'pre-line',
                                                lineHeight: '140%',
                                                fontWeight: '400',
                                                fontSize: '14px',
                                                color: theme.colors.grayLabelPlaceholder,
                                            }),
                                        }}
                                    />
                                )
                            }}
                        />
                        {errors.lbHouseholdMembersNumber && (
                            <SInputError
                                forElementId={'lbHouseholdMembersNumberField'}
                                sx={{
                                    fontFamily: fontFamily.body,
                                    color: theme.colors.redError,
                                }}
                            >
                                {t(errors.lbHouseholdMembersNumber?.message)}
                            </SInputError>
                        )}
                    </SBoxDropdown>
                </LBBox>
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                    }}
                >
                    {t('long_beach_household_income_members_relationship_to_applicant_description')}
                </LBText>
                <div className={style.household_applicant_container}>
                    <div className={style.household_applicant_container__icon}>
                        <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                    </div>
                    <div className={style.household_applicant_container__applicant_group}>
                        <LBText
                            sx={{
                                textAlignLast: 'left',
                                whiteSpace: 'pre-line',
                                fontFamily: fontFamily.body,
                                fontWeight: '700',
                                lineHeight: '140%',
                                color: theme.colors.lbBlue,
                                fontSize: '14px',
                                marginBottom: '5px',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_household_applicant_label'),
                            }}
                        />
                        <SInput
                            name="householdApplicantFullName"
                            id="householdApplicantFullName"
                            disabled={true}
                            value={`${applicationContext.profile?.firstName} ${applicationContext.profile?.lastName}`}
                            sx={{
                                fontFamily: fontFamily.body,
                                borderRadius: '8px',
                                marginBottom: '5px',
                            }}
                            style={{
                                fontSize: '14px',
                                fontWeight: 'normal',
                                lineHeight: '20px',
                                color: theme.colors.gray400,
                                backgroundColor: theme.colors.grayUnselectedCheckboxBackground,
                            }}
                        />
                        <SInput
                            name="householdApplicantRelationship"
                            id="householdApplicantRelationship"
                            disabled={true}
                            value={'Applicant'}
                            sx={{
                                fontFamily: fontFamily.body,
                                borderRadius: '8px',
                                marginBottom: '5px',
                            }}
                            style={{
                                fontSize: '14px',
                                fontWeight: 'normal',
                                lineHeight: '20px',
                                color: theme.colors.gray400,
                                backgroundColor: theme.colors.grayUnselectedCheckboxBackground,
                            }}
                        />
                    </div>
                </div>
                <div>
                    {Array.from({ length: householdMemberNumber }, (_, index) => (
                        <div key={index} className={style.household_member_container}>
                            <div className={style.household_member_container__icon}>
                                <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person_bw" />
                            </div>
                            <div className={style.household_member_container__member_group}>
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '700',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '5px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: t('long_beach_household_member_full_name_label'),
                                    }}
                                />
                                <SInput
                                    name={`householdMemberFullName-${index}`}
                                    id={`householdMemberFullName-${index}`}
                                    placeholder={t(
                                        'long_beach_household_income_household_member_full_name_placeholder'
                                    )}
                                    defaultValue={householdMembersDetails[index]?.name || ''}
                                    onChange={() => {
                                        handleHouseholdMemberDetails()
                                    }}
                                    ref={register({
                                        required: requiredString,
                                        minLength: {
                                            value: 2,
                                            message: 'error_name_at_least_two_characters',
                                        },
                                        pattern: {
                                            value: /^(?=.{2,25}$)[a-zA-ZÀ-ú-( )]+(?: [a-zA-ZÀ-ú-( )]+)?$/,
                                            message: 'error_special_characters_not_allowed',
                                        },
                                    })}
                                    sx={{
                                        fontFamily: fontFamily.body,
                                        borderRadius: '8px',
                                        marginBottom: '5px',
                                        borderColor: errors[`householdMemberFullName-${index}`]
                                            ? theme.colors.redError
                                            : theme.colors.inputBorder,
                                        '&::placeholder': {
                                            color: theme.colors.grayLabelPlaceholder,
                                        },
                                    }}
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 'normal',
                                        lineHeight: '20px',
                                        color: theme.colors.blackLabelfont,
                                    }}
                                />
                                <SBoxDropdown>
                                    <Controller
                                        name={`lbHouseholdMemberRelationship-${index}`}
                                        control={control}
                                        defaultValue={
                                            relationShipDropdownOptions.find(
                                                (option) => option.value === householdMembersDetails[index]?.relation
                                            ) || null
                                        }
                                        rules={{ required: 'required_field' as string }}
                                        render={(props: any) => {
                                            const { onChange, value } = props
                                            return (
                                                <Select
                                                    name={`lbHouseholdMemberRelationship-${index}`}
                                                    id={`lbHouseholdMemberRelationship-${index}`}
                                                    options={relationShipDropdownOptions}
                                                    placeholder={t(
                                                        'long_beach_household_member_relationship_placeholder'
                                                    )}
                                                    components={{ IndicatorSeparator: () => null }}
                                                    onChange={(option: any) => {
                                                        onChange(option)
                                                        handleHouseholdMemberDetails()
                                                    }}
                                                    value={value}
                                                    theme={(theme) => ({
                                                        ...theme,
                                                        colors: {
                                                            ...theme.colors,
                                                            primary: theme.colors.inputBorder,
                                                            neutral30: theme.colors.inputBorder,
                                                        },
                                                    })}
                                                    styles={{
                                                        ...sSelectStyle(theme),
                                                        container: (provided, state) => ({
                                                            ...sSelectStyle(theme).container(provided, state),
                                                            flex: 2,
                                                        }),
                                                        control: (provided, state) => ({
                                                            ...sSelectStyle(theme).control(provided, state),
                                                            fontFamily: fontFamily.body,
                                                            borderRadius: '8px',
                                                            borderColor: errors[
                                                                `lbHouseholdMemberRelationship-${index}`
                                                            ]
                                                                ? theme.colors.redError
                                                                : theme.colors.inputBorder,
                                                        }),
                                                        option: (provided, state) => ({
                                                            ...sSelectStyle(theme).option(provided, state),
                                                            textAlignLast: 'left',
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: '140%',
                                                            fontWeight: '400',
                                                            fontSize: '14px',
                                                            fontFamily: fontFamily.body,
                                                            color: theme.colors.blackLabelfont,
                                                            opacity: '80%',
                                                        }),
                                                        placeholder: (provided, state) => ({
                                                            ...sSelectStyle(theme).option(provided, state),
                                                            textAlignLast: 'left',
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: '140%',
                                                            fontWeight: '400',
                                                            fontSize: '14px',
                                                            color: theme.colors.grayLabelPlaceholder,
                                                        }),
                                                    }}
                                                />
                                            )
                                        }}
                                    />
                                    {(errors[`lbHouseholdMemberRelationship-${index}`] ||
                                        errors[`householdMemberFullName-${index}`]) && (
                                        <SInputError
                                            forElementId={`lbHouseholdMemberRelationship-${index}`}
                                            sx={{
                                                fontFamily: fontFamily.body,
                                                color: theme.colors.redError,
                                            }}
                                        >
                                            {t(errors[`lbHouseholdMemberRelationship-${index}`]?.message) ||
                                                t(errors[`householdMemberFullName-${index}`]?.message)}
                                        </SInputError>
                                    )}
                                </SBoxDropdown>
                            </div>
                        </div>
                    ))}
                </div>
                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    loading={addApplicationAnswersLoading || saveHouseholdMembersLoading}
                    title={t('save_and_continue')}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>
                {errors.householdIncomeMembers && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.householdIncomeMembers?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeMembers
